import type { Respuesta } from '@pasos/schemas';

/**
 * Borradores y cola de envío · el microcheck se responde sin conexión (S-08).
 *
 * Dos cosas distintas viven acá:
 *
 *   - **El borrador** de cada formulario: lo que el paciente lleva contestado.
 *     Se escribe en cada respuesta, así que cerrar la app en la pregunta 3 y
 *     volver a abrirla no pierde nada.
 *   - **La cola de envío**: las aplicaciones terminadas que no se pudieron
 *     mandar. Se vacían al reconectar.
 *
 * Van en `localStorage` y no en `sessionStorage` —al revés que el token de
 * sesión— por una razón concreta: el paciente puede responder el check-in,
 * cerrar el navegador y recuperar la conexión al día siguiente. Un borrador que
 * se borra al cerrar la pestaña no serviría para nada.
 *
 * **Lo que se guarda son ordinales y códigos de listas cerradas.** El catálogo
 * de tipos no admite texto libre, así que en el dispositivo no queda ni una
 * palabra escrita por el paciente ni un dato identificatorio.
 */

const PREFIJO_BORRADOR = 'pasos.formulario.';
/**
 * Dónde vive la cola en el teléfono. Exportada para el arnés de escenarios, que
 * siembra lo que un teléfono ya tenía guardado de un día anterior (B07): con la
 * misma clave y la misma forma, nunca con una copia que pueda envejecer.
 */
export const CLAVE_COLA = 'pasos.formularios.cola';

export type Borrador = {
  /** Id que asignó el servidor, si la aplicación llegó a abrirse. */
  aplicacionId: string | null;
  respuestas: Record<string, Respuesta>;
  /** Cuándo empezó, para poder medir la duración aunque se cierre la app. */
  iniciadoAt: number;
};

export type EnvioPendiente = {
  formularioId: string;
  hito: string | null;
  respuestas: Record<string, Respuesta>;
  cierre: 'completa' | 'parcial' | 'abandonada';
  duracionSegundos: number;
  /**
   * Cuándo terminó de responder, según el reloj del **dispositivo** (H-6).
   *
   * Viaja como dato y no manda: el servidor sigue decidiendo con su propio
   * reloj. Sirve para una sola cosa —que quien respondió en término sin señal
   * no quede marcado como tardío cuando su envío sale dos días después—, y el
   * servidor la acepta sólo dentro de los límites que él mismo verifica.
   *
   * Opcional porque un envío encolado por una versión anterior no la tiene: se
   * manda sin ella y vale la hora de llegada, como antes.
   */
  respondidoAt?: number;
  /**
   * El servidor lo rechazó de manera definitiva y **no se va a reintentar**.
   *
   * Se guarda en vez de borrarse: una respuesta del paciente no se tira en
   * silencio. Pero deja de contar como "esperando conexión" —el contacto se
   * vuelve a ofrecer, que es lo honesto— y no vuelve a salir en cada
   * reconexión. `codigo` es el del error de la API: una lista cerrada, sin nada
   * identificatorio.
   */
  rechazado?: { codigo: string; at: number };
};

function clave(formularioId: string, hito: string | null): string {
  return `${PREFIJO_BORRADOR}${formularioId}.${hito ?? 'sin-hito'}`;
}

/**
 * Lee del almacenamiento sin poder romper la pantalla.
 *
 * En modo privado, con el almacenamiento bloqueado o con una entrada corrupta de
 * una versión anterior, `localStorage` tira. Que eso deje al paciente sin poder
 * responder su check-in sería el peor resultado posible: se degrada a empezar de
 * cero, que es molesto pero funciona.
 */
function leer<T>(clave: string): T | null {
  try {
    const crudo = localStorage.getItem(clave);
    return crudo ? (JSON.parse(crudo) as T) : null;
  } catch {
    return null;
  }
}

function escribir(clave: string, valor: unknown): void {
  try {
    localStorage.setItem(clave, JSON.stringify(valor));
  } catch {
    // Sin almacenamiento el flujo sigue en memoria: se pierde al recargar y no
    // hay nada que mostrarle al paciente por eso.
  }
}

function borrar(clave: string): void {
  try {
    localStorage.removeItem(clave);
  } catch {
    // Ídem.
  }
}

// ── Borrador ────────────────────────────────────────────────────────────────

export function leerBorrador(formularioId: string, hito: string | null): Borrador | null {
  return leer<Borrador>(clave(formularioId, hito));
}

export function guardarBorrador(
  formularioId: string,
  hito: string | null,
  borrador: Borrador,
): void {
  escribir(clave(formularioId, hito), borrador);
}

export function borrarBorrador(formularioId: string, hito: string | null): void {
  borrar(clave(formularioId, hito));
}

// ── Cola de envío ───────────────────────────────────────────────────────────

/** Todo lo guardado, incluido lo rechazado. */
export function colaPendiente(): EnvioPendiente[] {
  return leer<EnvioPendiente[]>(CLAVE_COLA) ?? [];
}

/**
 * Lo que de verdad está esperando salir.
 *
 * Es la lista que mira todo lo demás: el banner, el Inicio y el propio vaciado.
 * Un envío rechazado sigue guardado y no está en ésta — no espera nada, porque
 * no va a salir.
 */
export function enviosPorSalir(): EnvioPendiente[] {
  return colaPendiente().filter((envio) => !envio.rechazado);
}

/**
 * Quién está mirando la cola.
 *
 * El Inicio tiene que poder decir "esto ya está guardado y sale solo" sin
 * consultarle nada al servidor —que es justo lo que no hay cuando no hay
 * conexión—, así que la cola es un almacén al que una pantalla se suscribe
 * (`useSyncExternalStore`). Sin esto, el Inicio leería la cola una vez al
 * montarse y seguiría ofreciendo el contacto como pendiente después de que el
 * vaciado lo mandó.
 *
 * La instantánea se cachea porque `useSyncExternalStore` compara por
 * identidad: devolver un array nuevo en cada lectura haría re-renderizar para
 * siempre.
 */
const mirandoLaCola = new Set<() => void>();
let instantanea: EnvioPendiente[] | null = null;

function laColaCambio(): void {
  instantanea = null;
  for (const escucha of mirandoLaCola) escucha();
}

export function suscribirseALaCola(escucha: () => void): () => void {
  mirandoLaCola.add(escucha);
  return () => {
    mirandoLaCola.delete(escucha);
  };
}

export function instantaneaDeLaCola(): EnvioPendiente[] {
  instantanea ??= enviosPorSalir();
  return instantanea;
}

/**
 * Marca un envío como rechazado definitivamente, con el código del error.
 *
 * Queda guardado: perder una respuesta en silencio es lo que este módulo
 * existe para evitar. Lo que cambia es que deja de prometer que va a salir.
 */
export function marcarRechazado(
  formularioId: string,
  hito: string | null,
  codigo: string,
  at: number = Date.now(),
): void {
  escribir(
    CLAVE_COLA,
    colaPendiente().map((envio) =>
      envio.formularioId === formularioId && envio.hito === hito
        ? { ...envio, rechazado: { codigo, at } }
        : envio,
    ),
  );
  laColaCambio();
}

/**
 * Encola un envío, reemplazando el del mismo formulario e hito si ya estaba.
 *
 * Reemplazar y no acumular: dos envíos del mismo hito son la misma aplicación
 * contestada dos veces, y mandar los dos crearía ruido que el índice único del
 * servidor va a rechazar igual. Gana el último, que es lo que el paciente dejó.
 */
export function encolar(envio: EnvioPendiente): void {
  const resto = colaPendiente().filter(
    (pendiente) => pendiente.formularioId !== envio.formularioId || pendiente.hito !== envio.hito,
  );
  escribir(CLAVE_COLA, [...resto, envio]);
  laColaCambio();
}

export function quitarDeLaCola(formularioId: string, hito: string | null): void {
  escribir(
    CLAVE_COLA,
    colaPendiente().filter(
      (pendiente) => pendiente.formularioId !== formularioId || pendiente.hito !== hito,
    ),
  );
  laColaCambio();
}

/**
 * Qué hacer con un envío según cómo falló.
 *
 *   - `se-queda`: el problema es transitorio o se arregla solo. Se reintenta.
 *   - `entregado`: falló, pero la respuesta **ya está** del otro lado.
 *   - `rechazado`: el servidor no lo va a aceptar nunca.
 */
export type QueHacerConElError = 'se-queda' | 'entregado' | 'rechazado';

export type ResultadoDelVaciado = {
  enviados: number;
  /** Los que siguen esperando de verdad: no incluye a los rechazados. */
  pendientes: number;
  rechazados: number;
};

/**
 * Vacía la cola mandando cada envío.
 *
 * **Lo que falla se queda, salvo que el servidor lo haya rechazado de verdad.**
 * Hasta E3.22 se quedaba todo, y eso era prudente mientras nadie leía la cola.
 * Ahora el Inicio la lee y le dice al paciente "tus respuestas están guardadas ·
 * no tenés que responder de nuevo": un envío que el servidor **nunca** va a
 * aceptar —un 422 después de un cambio de versión del formulario, un 403— lo
 * dejaría esperando para siempre una respuesta que ya no va a salir, con el
 * contacto venciendo y el protocolo de silencio escribiéndole a su persona de
 * contacto. Es el mismo daño que H-1, por otra puerta.
 *
 * Quién decide cuál es cuál no vive acá: este módulo es el almacén. La decisión
 * la toma quien conoce los errores de la API (`cola.ts`), y por eso llega como
 * parámetro. Por defecto se queda todo, que es la conducta prudente de siempre.
 */
export async function vaciarCola(
  enviar: (envio: EnvioPendiente) => Promise<void>,
  decidir: (error: unknown, envio: EnvioPendiente) => QueHacerConElError = () => 'se-queda',
): Promise<ResultadoDelVaciado> {
  let enviados = 0;
  let rechazados = 0;

  for (const envio of enviosPorSalir()) {
    try {
      await enviar(envio);
      quitarDeLaCola(envio.formularioId, envio.hito);
      borrarBorrador(envio.formularioId, envio.hito);
      enviados += 1;
    } catch (error) {
      const que = decidir(error, envio);
      if (que === 'entregado') {
        // Ya llegó por otro camino: sacarlo es lo correcto, y el borrador
        // también se va, porque no queda nada que retomar.
        quitarDeLaCola(envio.formularioId, envio.hito);
        borrarBorrador(envio.formularioId, envio.hito);
        enviados += 1;
      } else if (que === 'rechazado') {
        marcarRechazado(envio.formularioId, envio.hito, codigoDelError(error));
        rechazados += 1;
      }
      // `se-queda`: sigue en la cola para el próximo intento.
    }
  }

  return { enviados, pendientes: enviosPorSalir().length, rechazados };
}

/**
 * El código del error, para dejarlo anotado.
 *
 * Sale de la lista cerrada de códigos de la API; si viniera cualquier otra
 * cosa, se guarda una etiqueta genérica. Nunca el mensaje entero: no se guarda
 * en el dispositivo nada que no sea un código.
 *
 * Exportada porque el envío directo del formulario anota el mismo código que el
 * vaciado: es la misma marca, escrita por dos caminos que llevan al mismo lugar.
 */
export function codigoDelError(error: unknown): string {
  const codigo = (error as { codigo?: unknown } | null)?.codigo;
  return typeof codigo === 'string' ? codigo.slice(0, 40) : 'rechazado';
}
